import React, { useEffect } from 'react';
import {Alert, Button} from '@betareader/design-system';
// i18n
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
// custom
import ProgressChart from './progressChart';
import ReactionChart from './reactionChart';

import {
  fetchReadingData,
} from '../../../../modules/report';

import { authUtil } from '../../../../utils';

const colors = ['#FC9C04', '#FFBB28', '#5B5A54', '#000000', '#CCCCCC'];

const secondsToMinutes = (seconds) => `${Number(seconds / 60).toFixed(1)} min`;

const MinuteTick = ({ x, y, stroke, payload }) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={-12} textAnchor="end" fill="#999" fontSize="12">{secondsToMinutes(payload.value)}</text>
    </g>
  )
}

const ReaderTick = ({ x, y, stroke, payload }) => {
  const { t } = useTranslation();
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={-12} textAnchor="end" fill="#999" fontSize="12">{t('XReaders')}</text>
    </g>
  )
}

export const ReadingDataReport = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const currentBook = useSelector(state => state.book.currentBook);
  const versionData = useSelector(state => state.report.versionData);

  const fetchingReadingData = useSelector(state => state.report.fetchingReadingData);
  const invited = useSelector(state => state.report.invited);
  const started = useSelector(state => state.report.started);
  const finished = useSelector(state => state.report.finished);
  const abandoned = useSelector(state => state.report.abandoned);
  const readerFilters = useSelector(state => state.report.readerFilters);
  const versionFilter = useSelector(state => state.report.versionFilter);

  useEffect(() => {
    if (!!currentBook) {
      fetchReport();
    }
  }, [currentBook, readerFilters, versionFilter]);

  const fetchReport = async (type) => {
    const idToken = await authUtil.getFreshIdToken();
    if (!idToken) return;
    dispatch(fetchReadingData(idToken, currentBook._id, versionFilter, readerFilters, type));
  }

  const downloadReport = async () => {
    fetchReport('csv');
  }

  if (!versionData || !currentBook) {
    return null;
  }

  if (!(currentBook.content[0]?.parts?.filter(part => part.kind === 'Chapter')?.length > 0)) {
    return (
      <Alert>
        {t('YourBookDoesNotHaveAnyChaptersYet')}
      </Alert>
    );
  }
  if (fetchingReadingData) {
    return (
      <p role='status'>{t('Loading')}</p>
    );
  }

  return (
    <div>
      {versionData && (
        <section>
          <div style={{display: 'flex', justifyContent: 'flex-end'}}>
            <Button variant='secondary' onClick={downloadReport}>{t('Download')}</Button>
          </div>
          <dl style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', textAlign: 'center'}}>
            {[
              [t('Invited'), invited.readerCount],
              [t('Started'), started.readerCount],
              [t('Finished'), finished.readerCount],
              [t('Abandoned'), abandoned.readerCount],
            ].map(([label, count]) => (
              <div key={label}><dt>{label}</dt><dd style={{margin: 0, fontSize: '1.5rem'}}>{count}</dd></div>
            ))}
          </dl>
          <div style={{display: 'grid', gap: '1rem'}}>
              <section className='br-card' aria-label={t('ReaderProgress')}>
                <h2>{t('ReaderProgress')}</h2>
                {
                  versionData.map(version => (
                    <ProgressChart
                      key={`ReaderProgress_${version.versionId}`}
                      title={`${versionData.length > 1 ? `v${version.versionNumber}` : ''}`}
                      unit={t('Readers')}
                      dataKey='readerCount'
                      payloadKey='parts'
                      syncId='versionData'
                      data={version.readingData}
                      lineColor={colors[2]}
                      areaColor={colors[0]}
                    />
                  ))
                }
              </section>
              <section className='br-card' aria-label={t('AvgTimeToComplete')}>
                <h2>{t('AvgTimeToComplete')}</h2>
                {
                  versionData.map(version => (
                    <ProgressChart
                      key={`AvgTimeToComplete_${version.versionId}`}
                      title={`${versionData.length > 1 ? `v${version.versionNumber}` : ''}`}
                      unit={t('AvgTimeToComplete')}
                      dataKey='avgTimeSpent'
                      payloadKey='parts'
                      syncId='versionData'
                      data={version.readingData}
                      lineColor={colors[2]}
                      areaColor={colors[0]}
                      customValueParser={secondsToMinutes}
                      customTick={MinuteTick}
                    />
                  ))
                }
              </section>
              <section className='br-card' aria-label={t('Engagement')}>
                <h2>{t('Engagement')}</h2>
                {
                  versionData.map(version => (
                    <ReactionChart
                      key={`Engagement_${version.versionId}`}
                      title={`${versionData.length > 1 ? `v${version.versionNumber}` : ''}`}
                      name={t('Engagement')}
                      dataKey='avgTimeSpent'
                      payloadKey='parts'
                      syncId='versionData'
                      data={version.readingData}
                      areaColor={colors[0]}
                      customValueParser={secondsToMinutes}
                    />
                  ))
                }
              </section>
          </div>
        </section>
      )}
    </div>
  );
}

export default ReadingDataReport;
